/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* Periods as keyed by the stored period columns on srv_reqs and actions: '2026w40' or '2026-09'.
*
* Weeks start on Monday, week 1 being the week containing Jan 4th (ISO). The database's %u numbering
* agrees except for a week spanning New Year, which it splits into two - the January days become
* week 00. Those part weeks are never offered, an accepted gap.
*/
import { AbstractObject } from '@src/app/model/abstract-object';

export type PeriodType = 'week' | 'month';

const zeroPad = (num: number) => String(num).padStart(2, '0');
const DAY_MS = 24 * 60 * 60 * 1000;

export function isWeekPeriod(period: string) {
    return period.indexOf('w') > 0;
}

export function periodName(period: string): string {
    const year = +period.substring(0, 4);
    const prd = +period.substring(5, 7);

    if (!isWeekPeriod(period)) {
        return new Date(year, prd - 1).toLocaleString('default', { month: 'long' }) + ' ' + year;
    }
    const weekStart = weekMonday(year, prd);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const month = (date: Date) => date.toLocaleString('default', { month: 'short' });

    if (weekStart.getMonth() === weekEnd.getMonth()) {
        return weekStart.getDate() + '-' + weekEnd.getDate() + ' ' + month(weekStart) + ' ' + year;
    }
    return weekStart.getDate() + ' ' + month(weekStart) + ' - ' + weekEnd.getDate() + ' ' + month(weekEnd);
}

export function weekMonday(year: number, week: number): Date {
    const jan4th = new Date(year, 0, 4); // Jan 4th is always in week 1
    const jan4DOW = jan4th.getDay(); // getDay() is 0 for Sunday
    const monday = new Date(jan4th);
    monday.setDate(jan4th.getDate() - (jan4DOW === 0 ? 6 : jan4DOW - 1) + ((week - 1) * 7));
    return monday;
}

/** 53 week years start on a Thursday, or are leap years starting on a Wednesday - e.g. 2015, 2020, 2026 */
export function weeksInYear(year: number) {
    const jan1stDayOfWeek = new Date(year, 0, 1).getDay();
    const thursday = 4;
    const wednesday = 3;
    if (jan1stDayOfWeek === thursday || ((year % 4) === 0 && jan1stDayOfWeek === wednesday)) {
        return 53;
    }
    return 52;
}

export function priorPeriod(period: string): string {
    const year = +period.substring(0, 4);
    const prd = +period.substring(5, 7);
    const type = period.substring(4, 5);

    if (prd > 1) {
        return String(year) + type + zeroPad(prd - 1);
    }
    return isWeekPeriod(period) ? String(year - 1) + type + weeksInYear(year - 1) : String(year - 1) + '-12';
}

export function currentPeriod(type: PeriodType, today = new Date()): string {
    if (type === 'month') {
        return today.getFullYear() + '-' + zeroPad(today.getMonth() + 1);
    }
    // A week belongs to the year its Thursday falls in
    const thursday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 3 - ((today.getDay() + 6) % 7));
    const year = thursday.getFullYear();
    const daysIntoYear = Math.round((thursday.getTime() - weekMonday(year, 1).getTime()) / DAY_MS);
    return year + 'w' + zeroPad(1 + Math.floor(daysIntoYear / 7));
}

/** Newest first, starting with the period containing today */
export function recentPeriods(type: PeriodType, count: number): AbstractObject[] {
    const periods: AbstractObject[] = [];
    let period = currentPeriod(type);
    for (let i = 0; i < count; i++) {
        periods.push({ id: period, name: periodName(period) });
        period = priorPeriod(period);
    }
    return periods;
}
